import { getUserTimeZone } from '@northladder/utilities';

import {
  IExportDealsQueryOptions,
  IFetchDealsUrlOptions,
  IFetchDeviceGradeConditionUrlOptions,
  IFetchGradeDealsUrlOptions,
  IFetchGradeReviewUrlOptions,
  IFetchPaginatedAppraisalsPayload,
} from './types';

export const topBrandsUrl = () => '/report/top/brands';

/**
 * `topModelsUrl` is a function to generate endpoint for the top models.
 */
export const topModelsUrl = () => '/report/top/models';

export const inventorySummaryUrl = () => '/report/deals/summary';

/**
 * This endpoint retrieves uploads files needed for user registration.
 *
 * @deprecated - Please refer to the common `useImageVideoUploadMutation`
 */
export const uploadFileUrl = () => '/files/upload';

/**
 * This endpoint retrieves appraisal list.
 */
export const fetchAppraisalsUrl = ({
  count,
  page,
  userId,
  dealProcessType,
  listType,
  fromDate,
  toDate,
  categoryId,
  brandId,
  search,
}: IFetchPaginatedAppraisalsPayload) => {
  let url: string = `/appraisal?count=${count}&page=${page}&userId=${userId}&dealProcessType=${dealProcessType}&listType=${listType}`;

  if (fromDate && toDate) {
    url += `&fromDate=${fromDate}&toDate=${toDate}`;
    url += `&timeZone=${getUserTimeZone()}`;
  }

  if (categoryId) {
    url += `&categoryId=${categoryId}`;
  }

  if (brandId) {
    url += `&brandId=${brandId}`;
  }

  if (search) {
    url += `&search=${search}`;
  }

  return url;
};

/**
 * This endpoint retrieves download appraisals.
 */
export const downloadAppraisalsUrl = ({
  count,
  page,
  userId,
  dealProcessType,
  listType,
  fromDate,
  toDate,
  categoryId,
  brandId,
  search,
}: IFetchPaginatedAppraisalsPayload) => {
  let url: string = `/appraisal/export?count=${count}&page=${page}&userId=${userId}&dealProcessType=${dealProcessType}&listType=${listType}`;

  if (fromDate && toDate) {
    url += `&fromDate=${fromDate}&toDate=${toDate}`;
  }

  if (categoryId) {
    url += `&categoryId=${categoryId}`;
  }

  if (brandId) {
    url += `&brandId=${brandId}`;
  }

  if (search) {
    url += `&search=${search}`;
  }

  return url;
};

/**
 * This function generates the url for appraisal details endpoint.
 */
export const fetchAppraisalDetailsUrl = (appraisalId: string) =>
  `/appraisal/${appraisalId}`;

/**
 * This function generate the url for signed url.
 */
export const getSignedUrlEndpoint = () => '/secure/files/getSignedUrl';

/**
 * This endpoint retrieves bid execution time.
 */
export const getBidExecutionTimeUrl = () =>
  `/master-config-balance/bid-execution-time?timeZone=${getUserTimeZone()}`;

/**
 * This endpoint retrieves list of deals based on the appraisal code.
 */
export const dealsListUrl = ({
  brandIds,
  categoryId,
  count,
  field,
  filters,
  fromDate,
  groupIds,
  orderBy,
  page,
  searchText,
  sortBy,
  toDate,
}: IFetchDealsUrlOptions) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('count', count.toString());

  if (searchText) params.append('search', searchText);
  if (field) params.append('field', field);
  if (filters) params.append('filter', filters);
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);
  if (categoryId) params.append('categoryId', categoryId);
  if (brandIds) brandIds.forEach((id) => params.append('brandId', id));
  if (groupIds) groupIds.forEach((id) => params.append('groupId', id));
  if (orderBy) params.append('orderBy', orderBy);
  if (sortBy) params.append('sortBy', sortBy);

  const paramString = params.toString();

  return `/appraisal/dealer/appraisal-list${
    paramString ? `?${paramString}` : ''
  }`;
};

/**
 * This endpoint retrieves details of a deal.
 */
export const dealDetailsUrl = (appraisalCode: string) =>
  `appraisal/${appraisalCode}`;

/**
 * Credit limit endpoint for fetching credit limit under storeId
 */
export const creditLimitUrl = (storeId: string) =>
  `/b2b-data/credit-limit/details/${storeId}`;

/**
 * Device Grade Condition endpoint for fetching device grade condition using
 * appraisalCode
 */
export const DeviceGradeConditionUrl = ({
  appraisalCode,
}: IFetchDeviceGradeConditionUrlOptions) =>
  `/grade-review-request/asset-condition/${appraisalCode}`;

/**
 * Device Grade Condition endpoint for fetching device grade condition using
 * appraisalCode
 */
export const DeviceItemConditionUrl = ({
  appraisalCode,
}: IFetchDeviceGradeConditionUrlOptions) =>
  `/grade-review-request/item-condition/${appraisalCode}`;

/**
 * This endpoint retrieves grade review request list.
 */
export const fetchGradeReviewUrl = ({
  appraisalCode,
  count,
  page,
}: IFetchGradeReviewUrlOptions) =>
  `/grade-review-request/dealer?page=${page}&limit=${count}&searchAppraisalCode=${appraisalCode}`;

/**
 * This endpoint retrieves grade review request list.
 */
export const fetchDealsForGradeRequestSearchUrl = ({
  appraisalCode,
  count,
  page,
}: IFetchGradeDealsUrlOptions) => {
  if (appraisalCode) {
    return `/grade-review-request/dispute-appraisal?searchAppraisalCode=${appraisalCode}&page=${page}&limit=${count}`;
  }

  return `/grade-review-request/dispute-appraisal?page=${page}&limit=${count}`;
};

/**
 * This endpoint retrieves grade review request details.
 */
export const fetchGradeReviewDetailsUrl = (id: string) =>
  `/grade-review-request/${id}`;

/**
 * This endpoint retrieves grade review request list.
 */
export const fetchGradeReviewsListUrl = ({
  appraisalCode,
  count,
  fromDate,
  page,
  status,
  toDate,
}: IFetchGradeReviewUrlOptions) => {
  const params = new URLSearchParams({
    limit: count.toString(),
    page: page.toString(),
  });

  params.append('searchAppraisalCode', appraisalCode || '');
  params.append('status', status || '');

  if (fromDate) {
    params.append('fromDate', fromDate);
  }

  if (toDate) {
    params.append('toDate', toDate);
  }

  return `/grade-review-request/dealer?${params.toString()}`;
};

/**
 * This endpoint retrieves grade review request history.
 */
export const fetchGradeReviewHistoryUrl = (id: string) =>
  `/grade-review-request/history/${id}`;

/**
 * This endpoint used to create the grade change request.
 */
export const createGradeChangeRequestUrl = () => '/grade-review-request/dealer';

/**
 * This endpoint used to update the grade change request.
 */
export const updateGradeChangeRequestUrl = () =>
  '/grade-review-request/additional-info';

/**
 * Returns the URL for the export deals endpoint.
 */
export const fetchExportDealsExcelUrl = (
  queryParams: IExportDealsQueryOptions
) => {
  const params = new URLSearchParams();

  if (queryParams.searchKey) {
    params.append('searchKey', queryParams.searchKey);
  }

  if (queryParams.fromDate) {
    params.append('fromDate', queryParams.fromDate);
  }

  if (queryParams.toDate) {
    params.append('toDate', queryParams.toDate);
  }

  if (queryParams.categoryId) {
    params.append('categoryId', queryParams.categoryId);
  }

  if (queryParams.brandId) {
    queryParams.brandId.forEach((id) => params.append('brandId', id));
  }

  if (queryParams.groupId) {
    queryParams.groupId.forEach((id) => params.append('groupId', id));
  }

  if (queryParams.paymentType) {
    params.append('paymentType', queryParams.paymentType);
  }

  if (queryParams.courierStatus) {
    params.append('courierStatus', queryParams.courierStatus);
  }

  if (queryParams.filterBy) {
    queryParams.filterBy.forEach((filter) => params.append('filter', filter));
  }

  if (queryParams.searchType) {
    params.append('field', queryParams.searchType);
  }

  const queryStr = params.toString();

  return `/appraisal/dealer/appraisal-list/excel-export${
    queryStr ? `?${queryStr}` : ''
  }`;
};
